import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './styles.css';
import { Landing } from './pages/Landing';
import { CharacterMarket } from './pages/CharacterMarket';
import { CharacterChat } from './pages/CharacterChat';
import { DecorateScene } from './pages/DecorateScene';
import { Objects } from './pages/Objects';
import { Wallet } from './pages/Wallet';
import { Profile } from './pages/Profile';
import { Relationships } from './pages/Relationships';
import { RelationshipDetailPage } from './pages/RelationshipDetail';
import { RelationshipAlbum } from './pages/RelationshipAlbum';
import { Pomodoro } from './pages/Pomodoro';
import { SignInCallback } from './pages/SignInCallback';
import { Redeem } from './pages/Redeem';
import { Confirm } from './pages/Confirm';
import { ConfirmAccount } from './pages/ConfirmAccount';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { ForgotPassword } from './pages/ForgotPassword';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { NotFound } from './pages/NotFound';
import { I18nProvider } from './i18n';
import { VoiceFlagsProvider } from './lib/voice-flags';
import { AppHeader } from './components/AppHeader';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ActivityPopup } from './components/ActivityPopup';
import { FanProfileSetupModal } from './components/FanProfileSetupModal';
import { BottomNav } from './components/BottomNav';
import { setEmbedApiContext } from './embed/api/context';
import { getStoredFanIdToken } from './auth/fan-auth';

// Register the cloned embed runtime's API context once at boot. The
// EmbedRuntime + chat-client + voice clients underneath all read this
// context to learn how to hit the backend (absolute URL + auth headers).
//
// apiAuthHeaders MUST be synchronous, so we read whatever fan id-token
// is currently in storage. Background token refresh is driven by
// fan-api.ts's request() helper.
const apiBase = (
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env
    ?.VITE_API_BASE_URL ?? ''
).replace(/\/+$/, '');
setEmbedApiContext({
  apiUrl: (path: string) => {
    if (!path.startsWith('/api/')) {
      throw new Error(`apiUrl expects /api/... path, got "${path}"`);
    }
    return `${apiBase}${path}`;
  },
  apiAuthHeaders: (): Record<string, string> => {
    const tok = getStoredFanIdToken();
    return tok ? { Authorization: `Bearer ${tok}` } : {};
  },
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      {/* pb-20 on mobile reserves room for the persistent BottomNav
          (h-14 + safe-area-inset) so scrolled content never sits
          underneath it. sm:pb-0 removes the reserve once the top
          AppHeader's nav becomes visible and BottomNav is hidden. */}
      <main id="main" className="flex-1 pb-20 sm:pb-0">{children}</main>
      {/* Eligible-activity popup — self-fetches on mount when fan is
          signed in, and de-dupes via sessionStorage so it only shows
          once per tab session. */}
      <ActivityPopup />
      <BottomNav />
    </div>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');
ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <ErrorBoundary>
      <I18nProvider>
      <VoiceFlagsProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Shell><Landing /></Shell>} />
          {/* Auth flows — rendered without the Shell so the auth pages
              own the full viewport (their own gradient + logo card). */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/confirm-account" element={<ConfirmAccount />} />
          {/* Legal pages — public, no auth required. Linked from Signup
              checkbox + Chrome Web Store listing. */}
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/c/:characterId" element={<Shell><CharacterMarket /></Shell>} />
          {/* Chat route renders edge-to-edge — no AppHeader, no chrome.
              The cloned EmbedRuntime expects to own the full viewport
              (it draws its own back-button + chat controls). Wrapping
              it in Shell broke the nx-h-dvh height calculation. */}
          <Route path="/c/:characterId/chat" element={<CharacterChat />} />
          {/* Per `fan-scene-customization` — decorate page for owners. */}
          <Route path="/c/:characterId/decorate" element={<Shell><DecorateScene /></Shell>} />
          <Route path="/objects" element={<Shell><Objects /></Shell>} />
          <Route path="/wallet" element={<Shell><Wallet /></Shell>} />
          <Route path="/me" element={<Shell><Profile /></Shell>} />
          {/* fan-affinity-system surfaces — list + detail + album. */}
          <Route path="/relationships" element={<Shell><Relationships /></Shell>} />
          <Route path="/relationships/:characterId" element={<Shell><RelationshipDetailPage /></Shell>} />
          <Route path="/relationships/:characterId/album" element={<Shell><RelationshipAlbum /></Shell>} />
          {/* Pomodoro陪讀 mini-app. */}
          <Route path="/pomodoro/:characterId" element={<Shell><Pomodoro /></Shell>} />
          <Route path="/signin/callback" element={<SignInCallback />} />
          {/* Per `nfc-point-cards` — claim page (own viewport, no Shell). */}
          <Route path="/redeem" element={<Redeem />} />
          <Route path="/confirm" element={<Shell><Confirm /></Shell>} />
          <Route path="*" element={<Shell><NotFound /></Shell>} />
        </Routes>
        {/* Sits at router level so it overlays ALL routes including
            the chrome-less /c/:id/chat. Self-gates: renders null
            until it sees a signed-in fan without profileCompletedAt. */}
        <FanProfileSetupModal />
      </BrowserRouter>
      </VoiceFlagsProvider>
      </I18nProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
